import React, { useEffect } from "react";

import { useGetPatientInteractionsActions } from "~/providers/hooks";
import { usePreviewStore } from "~/providers/stores/usePreviewStore";
import { getConsultationSummaryV4Data } from "~/providers/utils/consultationRequests/patientPreview/getConsultationSummaryV4Data";
import { PatientIntakeConsultationSummaryV4 } from "~/providers/utils/types/patientIntakePreviewTypes";
import {
  PatientScreeningConsultationSummaryV4,
  SUMMARY_TYPE,
} from "~/providers/utils/types/patientPreviewTypes";
import { PatientPreviewTextInfo } from "../common";
import { Fillm } from "../common/Fillm";
import { PatientPreviewSelfReportedScoresV2 } from "../v2/selfReportedScores";
import { PatientPreviewCurrentMedicationsV2 } from "../v2/selfReportedScores/PatientPreviewCurrentMedicationsV2";
import { HealthHistoryV3 } from "../v3/HealthHistoryV3";
import { PersonalLifeHistoryV3 } from "../v3/PersonalLifeHistoryV3";
import { TreatmentRecommendationsV3 } from "../v3/TreatmentRecommendationsV3";
import { FunctionalDomainsV4 } from "./FunctionalDomainsV4";
import { ResponseThemes } from "./ResponseThemes";

interface PreviewSectionV4Props {
  screeningSummary: PatientScreeningConsultationSummaryV4;
  patientId: string;
  intakeSummary?: PatientIntakeConsultationSummaryV4;
}

export const PreviewSectionsV4 = ({
  patientId,
  screeningSummary,
  intakeSummary,
}: PreviewSectionV4Props) => {
  const {
    ai_informant_conversation_history,
    allergies,
    caseSummary,
    fillm,
    functionalDomains,
    healthHistory,
    intakeSummaryId,
    isSummaryLiked,
    isTreatmentRecommendationsLiked,
    medications,
    personalLifeHistory,
    responseThemes,
    scores,
    screeningSummaryId,
    treatmentRecommendations,
  } = getConsultationSummaryV4Data({ screeningSummary, intakeSummary });

  const { showRateButtons, summaryType, handleRateSummary, isRatePending } =
    useGetPatientInteractionsActions({
      patientId,
      screeningSummaryId,
      intakeSummaryId,
    });

  const { patientId: storePatientId, setPatientId, reset } = usePreviewStore();

  useEffect(() => {
    if (!storePatientId || storePatientId != patientId) {
      reset();
      setPatientId(patientId);
    }
  }, [patientId, storePatientId, setPatientId, reset]);

  return (
    <>
      <PatientPreviewTextInfo
        showRateButtons={showRateButtons}
        title="Case summary"
        info={caseSummary}
        summaryType={summaryType}
        onRate={handleRateSummary}
        isLike={isSummaryLiked}
        isRatePending={isRatePending}
      />

      <PatientPreviewCurrentMedicationsV2
        medications={medications}
        allergies={allergies}
      />

      {healthHistory && <HealthHistoryV3 {...healthHistory} />}

      {personalLifeHistory && (
        <PersonalLifeHistoryV3 {...personalLifeHistory} />
      )}

      {functionalDomains && (
        <FunctionalDomainsV4 functionalDomains={functionalDomains} />
      )}

      {ai_informant_conversation_history && (
        <Fillm
          aiConversationHistory={ai_informant_conversation_history}
          fillm={fillm}
        />
      )}
      {responseThemes?.some((rt) => rt.triggered) && (
        <ResponseThemes responseThemes={responseThemes} />
      )}

      {treatmentRecommendations && (
        <TreatmentRecommendationsV3
          treatmentRecommendations={treatmentRecommendations}
          summaryType={SUMMARY_TYPE.treatment_recommendations}
          onRate={handleRateSummary}
          isLike={isTreatmentRecommendationsLiked}
          isRatePending={isRatePending}
        />
      )}

      <PatientPreviewSelfReportedScoresV2 scores={scores} />
    </>
  );
};
